import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';
import imageCompression from 'browser-image-compression';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle } from '@fortawesome/free-solid-svg-icons';

import { Toaster, toast } from 'react-hot-toast';

// Add this function to fetch the nonce
function getNonce() {
    const nonceTag = document.querySelector('meta[name="csp-nonce"]');
    return nonceTag ? nonceTag.getAttribute('content') : '';
}

function PinEntry({ onPinSubmit }) {
    const [pin, setPin] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current.focus();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        onPinSubmit(DOMPurify.sanitize(pin));
    };

    return (
        <div className="pin-container">
            <form onSubmit={handleSubmit} className="pin-form">
                <input
                    type="password"
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    ref={inputRef}
                    value={pin}
                    onChange={(e) => setPin(DOMPurify.sanitize(e.target.value))}
                    className="pin-input"
                />
                <button type="submit" className="pin-submit-button">Login</button>
            </form>
        </div>
    );
}

const StickyNoteDetails = ({
    selectedBag, consumeCoffeeAmount, setConsumeCoffeeAmount, selectedMahlgrad, setSelectedMahlgrad,
    selectedTemperature, setSelectedTemperature, handleSaveDefaults, currentRating, handleRate,
    showSaveDefaults, comments, setShowCommentForm, transactionDate, setTransactionDate,
    onUnselectBag, onUpdateInitialAmount, onUpdateInitialPrice
}) => {
    const stickyNoteRef = useRef(null);
    const startYRef = useRef(0);
    const currentTranslateYRef = useRef(200); // Start at 200px down

    // Handle touch start
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        startYRef.current = touch.clientY;
        stickyNoteRef.current.style.transition = ''; // Remove transition during drag to avoid lag
    };

    // Handle touch move
    const handleTouchMove = (e) => {
        const touch = e.touches[0];
        const deltaY = touch.clientY - startYRef.current + currentTranslateYRef.current;

        if (deltaY >= 0 && deltaY <= 200) {
            window.requestAnimationFrame(() => {
                stickyNoteRef.current.style.transform = `translate(-50%,${deltaY}px)`;
            });
        }
    };

    // Handle touch end
    const handleTouchEnd = () => {
        const rect = stickyNoteRef.current.getBoundingClientRect();
        const screenHeight = window.innerHeight;

        if (screenHeight - rect.top > 200) {
            window.requestAnimationFrame(() => {
                stickyNoteRef.current.style.transition = 'transform 0.3s ease';
                stickyNoteRef.current.style.transform = 'translate(-50%,0px)';
                currentTranslateYRef.current = 0;
            });
        } else {
            window.requestAnimationFrame(() => {
                stickyNoteRef.current.style.transition = 'transform 0.3s ease';
                stickyNoteRef.current.style.transform = 'translate(-50%,200px)';
                currentTranslateYRef.current = 200;
            });
        }
    };

    // Handle mouse down
    const handleMouseDown = (e) => {
        startYRef.current = e.clientY;
        stickyNoteRef.current.style.transition = '';

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Handle mouse move
    const handleMouseMove = (e) => {
        const deltaY = e.clientY - startYRef.current + currentTranslateYRef.current;

        if (deltaY >= 0 && deltaY <= 200) {
            window.requestAnimationFrame(() => {
                stickyNoteRef.current.style.transform = `translate(-50%,${deltaY}px)`;
            });
        }
    };

    // Handle mouse up
    const handleMouseUp = () => {
        const rect = stickyNoteRef.current.getBoundingClientRect();
        const screenHeight = window.innerHeight;

        if (screenHeight - rect.top > 200) {
            window.requestAnimationFrame(() => {
                stickyNoteRef.current.style.transition = 'transform 0.3s ease';
                stickyNoteRef.current.style.transform = 'translate(-50%,0px)';
                currentTranslateYRef.current = 0;
            });
        } else {
            window.requestAnimationFrame(() => {
                stickyNoteRef.current.style.transition = 'transform 0.3s ease';
                stickyNoteRef.current.style.transform = 'translate(-50%,200px)';
                currentTranslateYRef.current = 200;
            });
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleEditInitialAmount = () => {
        // Simple prompt, or replace with a more elegant overlay
        const userInput = prompt('Enter new initial amount (grams)', selectedBag.initialAmount);
        if (userInput && !isNaN(userInput)) {
            onUpdateInitialAmount(selectedBag.id, parseInt(userInput));
        }
    };

    const handleEditInitialPrice = () => {
        // Prompt the user for a new price
        const userInput = prompt('Enter new initial price (in €)', selectedBag.totalPrice);
        if (userInput && !isNaN(userInput)) {
            // Call the passed in handler with the new price
            onUpdateInitialPrice(selectedBag.id, parseFloat(userInput));
        }
    };
    

    return (
        <div
            className="sticky-note"
            ref={stickyNoteRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown} // Add mouse down handler
        >
            <div className="handlebar"></div> {/* Handlebar for dragging */}
            <div className="sticky-header">
                <div className="coffee-meta"><b>{selectedBag.name}</b><br/>
                    <div className="coffee-meta-sub">Added by {selectedBag.addedBy} on {new Date(selectedBag.date).toLocaleDateString()}</div>
                </div>
                <button className="price-button" onClick={handleEditInitialAmount}><b>{selectedBag.initialAmount}g</b></button>
                <button className="price-button" onClick={handleEditInitialPrice}><b>€{selectedBag.totalPrice.toFixed(2)}</b></button>
                <button onClick={onUnselectBag} className="unselect-button"><b>x</b></button>
            </div>
            
            <div className="sticky-controls">
                <div className="control-group">
                    <button onClick={() => setConsumeCoffeeAmount(prev => Math.max(prev - 1, 1))}>-</button>
                    <span>{consumeCoffeeAmount}g</span>
                    <button onClick={() => setConsumeCoffeeAmount(prev => prev + 1)}>+</button>
                </div>
                <div className="control-group">
                    <button onClick={() => setSelectedMahlgrad(prev => Math.max(prev - 0.1, 0.1))}>-</button>
                    <span>M: {selectedMahlgrad.toFixed(1)}</span>
                    <button onClick={() => setSelectedMahlgrad(prev => prev + 0.1)}>+</button>
                </div>
                <div className="control-group">
                    <button onClick={() => setSelectedTemperature(prev => Math.max(prev - 1, 80))}>-</button>
                    <span>{selectedTemperature}°C</span>
                    <button onClick={() => setSelectedTemperature(prev => prev + 1)}>+</button>
                </div>
            </div>
            <div className="comment-section">
                {comments.length > 0 ? (
                    <div className="ticker-container">
                        <div className="ticker-content">
                            {comments.map((comment, index) => (
                                <span key={index} className="ticker-item">
                                    "{comment}"
                                </span>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="ticker-container">
                    <div className="ticker-content">
                            <span className="ticker-item">
                                No comments yet.
                            </span>
                    </div>
                </div>
                )}
                <div className="sticky-buttons">
                    <button onClick={setShowCommentForm} className="save-button">Add Comment</button>
                    {showSaveDefaults && <button onClick={handleSaveDefaults} className="save-button">Save Default Settings</button>}
                </div>
            </div>
            <div className="rating-container">  {/* Add a container div for the date and stars */}
                <input
                        type="date"
                        value={transactionDate}
                        onChange={(e) => setTransactionDate(e.target.value)}
                        className="transaction-date-input"
                />
                <StarRating currentRating={currentRating} onRate={handleRate} />
            </div>
        </div>
    );
}

function CoffeeBagCarousel({
    coffeeBags, selectedBag, onSelectBag, onShowAddCoffeeForm,
    onToggleShowEmptyBags, showEmptyBags, onUnselectBag,
}) {
    return (
        <div className="coffee-carousel-container">
            <div className="coffee-carousel">
                {coffeeBags
                    .filter(bag => showEmptyBags || bag.amount > 0)
                    .reverse()
                    .map(bag => (
                        <div
                            key={bag.id}
                            className={`carousel-item ${selectedBag === bag.id ? 'selected' : ''}`}
                            onClick={() => onSelectBag(bag.id)}
                        >
                            <img src={bag.imageUrl} alt={bag.name} />
                            <div className="carousel-item-details">
                                <div>{bag.name}</div>
                                <div className="progress-bar-container">
                                    <div className="progress-bar" style={{ width: `${(bag.amount / bag.initialAmount) * 100}%` }}></div>
                                </div>
                                <div className="progress-bar-text">
                                    <span>{bag.amount}g left</span>
                                    <span  className="carousel-star-text">{bag.rating ? ' ★'+ bag.rating.toFixed(1) : ''}</span>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    );
}

function StarRating({ currentRating, onRate }) {
    const [rating, setRating] = useState(currentRating || 0);

    useEffect(() => {
        setRating(currentRating || 0);
    }, [currentRating]);

    const handleRatingClick = (newRating) => {
        setRating(newRating);
        onRate(newRating);
    }; // <-- Missing closing brace was here

    return (
        <div className="star-rating">
            {[1, 2, 3, 4, 5].map((star) => (
                <span
                    key={star}
                    className={star <= rating ? 'star filled' : 'star empty'}
                    onClick={() => handleRatingClick(star)}
                >
                    &#9733;
                </span>
            ))}
        </div>
    );
}


function App() {
    const [data, setData] = useState({
        coffeePot: 0,
        coffeeBags: [],
        people: [],
        transactions: []
    });
    const [showAddCoffee, setShowAddCoffee] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [consumeCoffeeAmount, setConsumeCoffeeAmount] = useState('18');
    const [selectedBag, setSelectedBag] = useState('');
    const [newUserName, setNewUserName] = useState('');
    const [showAddUserOverlay, setShowAddUserOverlay] = useState(false);
    const [addCoffeeAmount, setAddCoffeeAmount] = useState('250');
    const [coffeePrice, setCoffeePrice] = useState('10.0');
    const [coffeeName, setCoffeeName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [authenticated, setAuthenticated] = useState(localStorage.getItem('authenticated') === 'true');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
    const [showEmptyBags, setShowEmptyBags] = useState(false);
    const [manualTransactionAmount, setManualTransactionAmount] = useState('');
    const [manualTransactionMessage, setManualTransactionMessage] = useState('');
    const [showManualTransactionForm, setShowManualTransactionForm] = useState(false);
    const [currentRating, setCurrentRating] = useState(0); 
    const [selectedMahlgrad, setSelectedMahlgrad] = useState(0.6); // State for Mahlgrad
    const [selectedTemperature, setSelectedTemperature] = useState(94); // State for Temperature
    const [showSaveDefaults, setShowSaveDefaults] = useState(false);
    const [comments, setComments] = useState([]);
    const [randomComment, setRandomComment] = useState('');
    const [showCommentForm, setShowCommentForm] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);


    const handleUpdateInitialAmount = async (bagId, newAmount) => {
        try {
            await axios.post('/api/update-initial-amount', {
                coffeeBagId: bagId,
                newInitialAmount: newAmount
            });
            showToast('Initial amount updated successfully.', 'success');
            fetchData();
        } catch (error) {
            console.error('Error updating initial amount:', error);
            showToast('Error updating initial amount.', 'error');
        }
    };
    
    const handleUpdateInitialPrice = async (bagId, newPrice) => {
        try {
            await axios.post('/api/update-initial-price', {
                coffeeBagId: bagId,
                newTotalPrice: newPrice
            });
            showToast('Initial price updated successfully.', 'success');
            fetchData(); // Refresh the data to update the UI
        } catch (error) {
            console.error('Error updating initial price:', error);
            showToast('Error updating initial price.', 'error');
        }
    };
    
    
    const handleCommentSubmit = () => {
        if (newComment.trim()) {
            handleAddComment(newComment.trim());
            setNewComment('');  // Clear the input after submission
            setShowCommentForm(false); // Close the overlay after submission
        }
    };

    const handleShowManualTransactionForm = () => {
        if (data.people.length > 0) {
            setSelectedUser(data.people[0].name); 
        }
        setShowManualTransactionForm(true);
    };

    const handleAddComment = async (comment) => {
        try {
            const response = await axios.post('/api/add-comment', {
                coffeeBagId: selectedBag,
                comment: DOMPurify.sanitize(comment),
            });
    
            if (response.data.success) {
                const updatedComments = [...comments, comment]; // Add the new comment to the existing comments
                setComments(updatedComments); // Update the state with the new comment list
                setRandomComment(updatedComments[Math.floor(Math.random() * updatedComments.length)]); // Update the random comment
                showToast('Comment added successfully.', 'success');
            } else {
                showToast('Failed to add comment.', 'error');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            showToast('Error adding comment.', 'error');
        }
    };
    

    
    const handleHideManualTransactionForm = () => {
        setShowManualTransactionForm(false);
    };

    const resetManualTransactionForm = () => {
        setManualTransactionAmount('');
        setManualTransactionMessage('');
        setTransactionDate(new Date().toISOString().split('T')[0]); 
    };

    const LoadingOverlay = () => (
        <div className="loading-overlay">
            <div className="spinner"></div>
        </div>
    );
    
    useEffect(() => {
        if (loading) {
            document.body.classList.add('loading');
        } else {
            document.body.classList.remove('loading');
        }
    }, [loading]);
    
    useEffect(() => {
        if (authenticated) {
            fetchData();
        }
    }, [authenticated]);

    useEffect(() => {
        if (data.coffeeBags) {
            const visibleBags = data.coffeeBags.filter(bag => showEmptyBags || bag.amount > 0);

            if (!showEmptyBags && selectedBag && !visibleBags.some(bag => bag.id === selectedBag)) {
                if (visibleBags.length === 1) {
                    setSelectedBag(visibleBags[0].id);
                } else {
                    setSelectedBag('');
                }
            } else if (!selectedBag) {
                if (visibleBags.length === 1) {
                    setSelectedBag(visibleBags[0].id);
                } else {
                    setSelectedBag('');
                }
            }
        } else {
            setSelectedBag('');
        }
    }, [data.coffeeBags, showEmptyBags, selectedBag]);

    useEffect(() => {
        if (selectedBag) {
            const selectedBagDetails = data.coffeeBags.find(bag => bag.id === selectedBag);
            if (selectedBagDetails) {
                setConsumeCoffeeAmount(selectedBagDetails.defaultAmount);
                setSelectedMahlgrad(selectedBagDetails.defaultMahlgrad);
                setSelectedTemperature(selectedBagDetails.defaultTemperature);
                setComments(selectedBagDetails.comments || []); // Ensure comments are set here
                setCurrentRating(0); // Reset the star rating to 0
            }
        }
    }, [selectedBag, data.coffeeBags]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const selectedBagDetails = data.coffeeBags.find(bag => bag.id === selectedBag);
            if (selectedBagDetails) {
                const isModified = 
                    parseInt(consumeCoffeeAmount) !== selectedBagDetails.defaultAmount ||
                    parseFloat(selectedMahlgrad).toFixed(1) !== selectedBagDetails.defaultMahlgrad.toFixed(1) ||
                    parseInt(selectedTemperature) !== selectedBagDetails.defaultTemperature;
                
                setShowSaveDefaults(isModified);
            }
        }, 100); // 200 milliseconds delay
    
        return () => clearTimeout(timeoutId); // Cleanup timeout if dependencies change or component unmounts
    }, [consumeCoffeeAmount, selectedMahlgrad, selectedTemperature, selectedBag]);
    

    useEffect(() => {
        const updateInterval = 5000; // 5 seconds interval
    
        const updateComment = () => {
            const newComment = comments[Math.floor(Math.random() * comments.length)];
            setRandomComment(newComment);
        };
    
        // Immediately show the first comment
        if (comments.length > 0) {
            const firstComment = comments[Math.floor(Math.random() * comments.length)];
            setRandomComment(firstComment);
        }
    
        // Set an interval to update the comment every 5 seconds
        const intervalId = setInterval(updateComment, updateInterval);
    
        // Cleanup function to clear the interval
        return () => {
            clearInterval(intervalId);
        };
    }, [comments]);
    
        
    
    
    const fetchData = async () => {
        setLoading(true); // Set loading to true before the request starts
        try {
            const result = await axios.get('/api/data');
            const fetchedData = result.data || { coffeePot: 0, coffeeBags: [], people: [], transactions: [] };
    
            setData(fetchedData);
    
            if (selectedBag && fetchedData.coffeeBags.some(bag => bag.id === selectedBag)) {
                setSelectedBag(selectedBag);
            } else if (fetchedData.coffeeBags.length === 1) {
                const lowestAmountBag = fetchedData.coffeeBags.reduce((prev, curr) => {
                    return prev.amount < curr.amount ? prev : curr;
                });
                setSelectedBag(lowestAmountBag.id);
            } else {
                setSelectedBag('');
            }
    
            if (selectedBag) {
                const bagDetails = fetchedData.coffeeBags.find(bag => bag.id === selectedBag);
                if (bagDetails) {
                    setCurrentRating(0); // Set current rating from fetched data
                    setComments(bagDetails.comments || []); // Set comments directly from the coffee bag data
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            if (error.response && error.response.status === 401) {
                showToast('Session expired. Please log in again.', 'error');
                handleLogout();
            } else {
                setData({ coffeePot: 0, coffeeBags: [], people: [], transactions: [] });
                showToast('Database not accessible: Either you spammed the Database (100 requests per 15 Minutes) or the Backend is misconfigured. Try again in 15 minutes.', 'error');
            }
        } finally {
            setLoading(false); // Set loading to false after the request completes
        }
    };
    
      
    const handlePinSubmit = async (pin) => {
        try {
            const response = await axios.post('/api/verify-pin', { pin: DOMPurify.sanitize(pin) });

            if (response.data.success) {
                localStorage.setItem('authenticated', true);
                setAuthenticated(true);
                fetchData();
            } else {
                showToast('Incorrect PIN. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error verifying PIN:', error);
            showToast('Error verifying PIN.', 'error');
        }
    };

    const handleLogout = () => {
        setAuthenticated(false);
        setIsAdmin(false);
        localStorage.removeItem('authenticated');
    };

    const handleSaveDefaults = async () => {
        if (!selectedBag) return;
        try {
            await axios.post('/api/save-defaults', {
                coffeeBagId: selectedBag,
                defaultAmount: parseInt(consumeCoffeeAmount),
                defaultMahlgrad: parseFloat(selectedMahlgrad).toFixed(1),
                defaultTemperature: parseInt(selectedTemperature),
            });
            showToast('Successfully saved default values for this Coffee.', 'success');
            fetchData(); // Reload data to reflect the updated defaults
            setShowSaveDefaults(false); // Hide the button after saving
        } catch (error) {
            console.error('Error saving defaults:', error);
            showToast('Error saving defaults.', 'error');
        }
    };
    
    

    
    const handleImageChange = (e) => {
        setSelectedImage(e.target.files[0]);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredTransactions = data.transactions.filter(transaction =>
        (transaction.message || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getTimestamp = (selectedDate) => {
        return selectedDate === new Date().toISOString().split('T')[0] ?
            new Date().toISOString() :
            new Date(`${selectedDate}T00:00:00`).toISOString();
    };

    const handleConsume = async (personName) => {
        const timestamp = getTimestamp(transactionDate);
        
        if (!selectedBag) {
            showToast('Please select a coffee bag first.', 'error');
            return;
        }
    
        const selectedBagDetails = data.coffeeBags.find(bag => bag.id === selectedBag);
    
        if (selectedBagDetails && selectedBagDetails.amount < consumeCoffeeAmount) {
            showToast('Not enough coffee left in the selected bag.', 'error');
            return;
        }
    
        const costPerGram = selectedBagDetails.totalPrice / selectedBagDetails.initialAmount;
        const consumedCost = costPerGram * parseInt(consumeCoffeeAmount);
        const transactionCost = consumedCost.toFixed(2);
    
        setLoading(true); // Set loading to true before the request starts
        try {
            await axios.post('/api/consume', {
                person: personName,
                amount: parseInt(consumeCoffeeAmount),
                coffeeBagId: selectedBagDetails.id,
                date: timestamp,
                rating: currentRating > 0 ? currentRating : null,
                mahlgrad: selectedMahlgrad,
                temperature: selectedTemperature
            });
            showToast(`${personName}: -€${transactionCost}.`, 'success');
            fetchData();
            setSelectedBag(selectedBagDetails.id);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                showToast(error.response.data.message, 'error');
            } else {
                console.error('Error consuming coffee:', error);
                showToast('Error consuming coffee.', 'error');
            }
        } finally {
            setLoading(false); // Set loading to false after the request completes
        }
    };
    

    const handleAddUserClick = () => {
        setShowAddUserOverlay(true);
    };

    const handleAddUser = async () => {
        if (newUserName) {
            try {
                await axios.post('/api/add-user', { user: newUserName });
                setNewUserName('');
                setShowAddUserOverlay(false);
                fetchData();
            } catch (error) {
                console.error('Error adding user:', error);
                showToast('Error adding user.', 'error');
            }
        }
    };

    const handleDeleteUser = async (userName) => {
        const user = data.people.find(p => p.name === userName);
    
        if (Math.abs(user.balance) > 0.01) {
            showToast(`User "${userName}" cannot be deleted unless the balance is 0.00.`, 'error');
            return;
        }
    
        try {
            const response = await axios.post('/api/delete-user', { user: userName });
            if (response.status === 200) {
                showToast('User deleted successfully.', 'success');
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                showToast(error.response.data.message, 'error');
            } else {
                console.error('Error deleting user:', error);
                showToast('Error deleting user.', 'error');
            }
        }
    };

    const handleDeleteTransaction = async (transactionId) => {
        setLoading(true); // Set loading to true before the request starts
        try {
            const response = await axios.post('/api/delete-transaction', { transactionId });
            if (response.status === 200) {
                fetchData();
                showToast('Transaction deleted successfully.', 'success');
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                showToast(error.response.data.message, 'error');
            } else {
                console.error('Error deleting transaction:', error);
                showToast('Error deleting transaction.', 'error');
            }
        } finally {
            setLoading(false); // Set loading to false after the request completes
        }
    };
    

    const handleShowEmptyBagsToggle = () => {
        setShowEmptyBags(prev => !prev);
    };

    const showToast = (message, type) => {
        if (type === 'success') {
          toast.success(message);
        } else if (type === 'error') {
          toast.error(message);
        } else {
          toast(message); // For general notifications
        }
      };

    const handleAddCoffee = async () => {
        if (isSubmitting) return;

        if (!selectedUser) {
            showToast('Please select a user.', 'error');
            return;
        }

        if (!addCoffeeAmount || addCoffeeAmount <= 0) {
            showToast('Please enter a valid coffee amount.', 'error');
            return;
        }

        if (!coffeePrice || coffeePrice <= 0) {
            showToast('Please enter a valid price.', 'error');
            return;
        }

        if (!coffeeName) {
            showToast('Please enter a coffee name.', 'error');
            return;
        }

        setIsSubmitting(true);

        let compressedFile = selectedImage;
        if (selectedImage) {
            try {
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 512,
                    useWebWorker: true,
                };
                compressedFile = await imageCompression(selectedImage, options);

                const fileExtension = selectedImage.name.split('.').pop();
                const newFileName = selectedImage.name.replace(/\.[^/.]+$/, "");
                compressedFile = new File([compressedFile], `${newFileName}.${fileExtension}`, {
                    type: selectedImage.type,
                    lastModified: Date.now(),
                });

            } catch (error) {
                console.error('Error compressing image:', error);
                showToast('Error compressing image.', 'error');
                setIsSubmitting(false);
                return;
            }
        }

        const timestamp = getTimestamp(transactionDate);

        const formData = new FormData();
        formData.append('provider', selectedUser);
        formData.append('amount', parseInt(addCoffeeAmount));
        formData.append('price', parseFloat(coffeePrice));
        formData.append('image', compressedFile);
        formData.append('name', coffeeName);
        formData.append('date', timestamp);

        try {
            await axios.post('/api/add-coffee', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setShowAddCoffee(false);
            setSelectedUser('');
            setAddCoffeeAmount('250');
            setCoffeePrice('10');
            setSelectedImage(null);
            setCoffeeName('');
            fetchData();
            
            showToast(`${coffeeName} added successfully.`, 'success');
        } catch (error) {
            console.error('Error adding coffee:', error);
            showToast('Error adding coffee.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddManualTransaction = async () => {
        if (!selectedUser) {
            showToast('Please select a user.', 'error');
            return;
        }
    
        if (!manualTransactionAmount) {
            showToast('Please enter a transaction amount.', 'error');
            return;
        }
    
        if (!manualTransactionMessage) {
            showToast('Please enter a transaction message.', 'error');
            return;
        }
    
        const timestamp = getTimestamp(transactionDate);
    
        try {
            await axios.post('/api/add-manual-transaction', {
                person: selectedUser,
                amount: parseFloat(manualTransactionAmount),
                message: selectedUser + ': ' + manualTransactionMessage + ' ' + manualTransactionAmount + ' €',
                date: timestamp,
            });
            showToast('Transaction added successfully.', 'success');
            resetManualTransactionForm(); 
            setShowManualTransactionForm(false); 
            fetchData(); 
        } catch (error) {
            console.error('Error adding manual transaction:', error);
            showToast('Error adding manual transaction.', 'error');
        }
    };

    const generateRandomName = () => {
        const adjectives = [
            "Smooth", "Bold", "Mellow", "Rich", "Dark", "Velvety", "Creamy", "Silky", "Spicy", "Zesty",
            "Warm", "Deep", "Hearty", "Nutty", "Earthy", "Robust", "Crisp", "Sweet", "Fragrant", "Lively",
            "Full-bodied", "Aromatic", "Balanced", "Intense", "Subtle", "Bright", "Vibrant", "Sleek",
            "Gritty", "Buttery", "Breezy", "Crisp", "Fiery", "Bitter", "Caramel", "Golden", "Toasty",
            "Ripe", "Honeyed", "Lush", "Spirited", "Bold", "Invigorating", "Tart", "Silken", "Energizing",
            "Complex", "Elegant", "Fresh", "Glorious", "Light", "Majestic", "Opulent", "Powerful",
            "Refreshing", "Satisfying", "Sturdy", "Vigorous", "Wholesome", "Youthful", "Exotic",
            "Lavish", "Mystic", "Peppery", "Radiant", "Resonant", "Sublime", "Tempting"
        ];

        const nouns = [
            "Blend", "Roast", "Brew", "Bean", "Java", "Cup", "Sip", "Shot", "Crema", "Latte", "Espresso",
            "Macchiato", "Mocha", "Cappuccino", "Americano", "Flat White", "Cortado", "Affogato",
            "Drip", "Pour-over", "Cold Brew", "French Press", "Turkish", "AeroPress", "Chemex", "Café",
            "Beverage", "Elixir", "Potion", "Wake-Up", "Fix", "Infusion", "Tonic", "Revival", "Essence",
            "Concoction", "Potion", "Delight", "Extract", "Blend", "Decoction", "Brew", "Concentrate",
            "Flavor", "Sensation", "Aroma", "Ambrosia", "Creation", "Jolt", "Kick", "Stimulant", "Invigoration",
            "Pick-me-up", "Punch", "Thrill", "Nectar", "Balm", "Relish", "Treat", "Fixation", "Addiction"
        ];

        const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
        const noun = nouns[Math.floor(Math.random() * nouns.length)];
        return `${adjective} ${noun}`;
    }

    const getUserWithLowestBalance = () => {
        if (data.people.length === 0) return '';
        return data.people.reduce((minUser, user) =>
            user.balance < minUser.balance ? user : minUser, data.people[0]
        ).name;
    };

    const showAddCoffeeForm = () => {
        const userWithLowestBalance = getUserWithLowestBalance();
        setSelectedUser(userWithLowestBalance);
        setTransactionDate(new Date().toISOString().split('T')[0]);
        setCoffeeName(generateRandomName());
        setShowAddCoffee(true);
    };

    const calculatePercentage = (amount, initialAmount) => {
        return (amount / initialAmount) * 100;
    };

    const downloadJsonFile = async () => {
        try {
            const response = await axios.get('/api/data', {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'uploads/data.json');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading the file:', error);
        }
    };

    const handleAdminClick = async () => {
        try {
            setIsAdmin(prev => !prev);
            // showToast(`Admin mode ${isAdmin ? 'disabled' : 'enabled'}.`, isAdmin ? 'error' : 'success');
        } catch (error) {
            console.error('Error during admin login:', error);
            showToast('Error during admin login.', 'error');
        }
    };

    const handleRate = async (rating) => {
            setCurrentRating(rating);  // Update current rating after successful backend call
    };
    

    const handleMahlgradChange = (newMahlgrad) => {
        setSelectedMahlgrad(parseFloat(newMahlgrad.toFixed(1))); // Ensure it's a number and fixed to 1 decimal place
    };
    

    const handleTemperatureChange = (newTemperature) => {
        setSelectedTemperature(newTemperature); // Update Temperature
    };


    return (
        <div className="container">
            <Toaster
                toastOptions={{
                    duration: 5000, // Set default duration for all toasts to 5 seconds
                    nonce: getNonce(), // Add your nonce here
                }}
            />
            {loading && <LoadingOverlay />}
            {!authenticated ? (
                <PinEntry onPinSubmit={handlePinSubmit} />
            ) : (
                <>
                    {showAddCoffee && <div className="overlay" onClick={() => setShowAddCoffee(false)}></div>}
                    <div className="box">
                        <section className="coffee-status">
                            <div className="coffee-bags">
                                <button onClick={showAddCoffeeForm} className="add-user-button"><b>+</b> 🫘</button>
                                &nbsp;<button onClick={handleShowEmptyBagsToggle} className="add-user-button">
                                    {showEmptyBags ? "Hide Empty Bags" : "Show Empty Bags"}
                                </button>
                                <CoffeeBagCarousel
                                    coffeeBags={data.coffeeBags}
                                    selectedBag={selectedBag}
                                    onSelectBag={setSelectedBag}
                                    onShowAddCoffeeForm={() => setShowAddCoffee(true)}
                                    onToggleShowEmptyBags={() => setShowEmptyBags(!showEmptyBags)}
                                    showEmptyBags={showEmptyBags}
                                    onUnselectBag={() => setSelectedBag(null)}
                                />
                            </div>
                        </section>
                    </div>
                    <div className="box2">
                    <br/>
                        {selectedBag && (
                                                <StickyNoteDetails
                                                    selectedBag={data.coffeeBags.find(bag => bag.id === selectedBag)}
                                                    consumeCoffeeAmount={consumeCoffeeAmount}
                                                    setConsumeCoffeeAmount={setConsumeCoffeeAmount}
                                                    selectedMahlgrad={selectedMahlgrad}
                                                    setSelectedMahlgrad={setSelectedMahlgrad}
                                                    selectedTemperature={selectedTemperature}
                                                    setSelectedTemperature={setSelectedTemperature}
                                                    handleSaveDefaults={handleSaveDefaults}
                                                    currentRating={currentRating}
                                                    handleRate={handleRate}
                                                    showSaveDefaults={showSaveDefaults}
                                                    comments={comments}
                                                    setShowCommentForm={setShowCommentForm}
                                                    randomComment={randomComment}
                                                    // isAdmin={isAdmin}
                                                    transactionDate={transactionDate}
                                                    setTransactionDate={setTransactionDate}
                                                    // onDeleteTransaction={handleDeleteTransaction}
                                                    // onLogout={handleLogout}
                                                    // onToggleEditMode={() => setIsAdmin(!isAdmin)}
                                                    onUnselectBag={() => setSelectedBag(null)}
                                                    onUpdateInitialAmount={handleUpdateInitialAmount}
                                                    onUpdateInitialPrice={handleUpdateInitialPrice}
                                                />
                                            )}
                        <section className="people-section">
                            <ul className="people-list">
                                {data.people && data.people.length > 0 ? (
                                    data.people.map((person, index) => (
                                        <li key={index}>
                                            <span className="user-name">
                                                <FontAwesomeIcon icon={faUserCircle} className="user-avatar" />
                                                {person.name}
                                                <span className="user-counter">({person.counter})</span>
                                            </span>
                                            <span
                                                className={`balance ${person.balance < 0 ? 'negative' : 'positive'}`}
                                            >
                                            {person.balance < 0 ? `-€${Math.abs(person.balance).toFixed(2)}` : `€${person.balance.toFixed(2)}`}
                                        </span>
                                        {selectedBag && (
                                            <>
                                                <button onClick={() => handleConsume(person.name)} className="add-to-basket-button">
                                                    {/* -€{((selectedBag.totalPrice / selectedBag.initialAmount) * consumeCoffeeAmount).toFixed(2)} */}
                                                    <b>-&nbsp;€{((data.coffeeBags.find(bag => bag.id === selectedBag)?.totalPrice / data.coffeeBags.find(bag => bag.id === selectedBag)?.initialAmount) * consumeCoffeeAmount).toFixed(2)}</b>
                                                    
                                                    {currentRating > 0 && (
                                                        <span className="filled" style={{ fontSize: '0.8em' }}>
                                                            &nbsp;&nbsp;{currentRating}
                                                            &#9733;
                                                        </span>
                                                    
                                                    )}
                                                    </button>
                                                </>
                                        )}
                                                {isAdmin && (
                                                    <>
                                                        <button 
                                                            className="delete-user-button" 
                                                            onClick={() => handleDeleteUser(person.name)}
                                                        >
                                                            🗑️
                                                        </button>
                                                    </>
                                                )}
                                            
                                    </li>
                                ))
                            ) : (
                                <p>No people available</p>
                            )}
                        </ul>
                        {isAdmin && (<button onClick={handleAddUserClick} className="add-user-button"><b>+</b> User</button>)}&nbsp;
                        {isAdmin && (<button className="add-user-button" onClick={handleShowManualTransactionForm}>+/- Balance</button>)}
                    </section>

                        {showAddCoffee && (
                            <section className="add-coffee-section">
                                <h2>Add Coffee</h2>
                                <div className="add-coffee-form">
                                    <label htmlFor="user-select">Coffee brought by</label>
                                    <select
                                        value={selectedUser} 
                                        onChange={(e) => setSelectedUser(e.target.value)}
                                    >
                                        {data.people.map((person, index) => (
                                            <option key={index} value={person.name}>
                                                {person.name}
                                            </option>
                                        ))}
                                    </select>

                                    <label htmlFor="coffee-image" className="custom-file-upload">
                                    {selectedImage ? '✅ Photo added' : '📷 Add Photo (optional)'}
                                </label>
                                <input
                                    id="coffee-image"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />

                                <label htmlFor="coffee-name">Coffee Name</label>
                                <input
                                    id="coffee-name"
                                    type="text"
                                    placeholder="Enter Coffee Name"
                                    value={coffeeName}
                                    onChange={(e) => setCoffeeName(e.target.value)}
                                />

                                    <label htmlFor="coffee-amount">Amount of Coffee</label>
                                    <div className="input-with-unit">
                                        <input
                                            id="coffee-amount"
                                            type="number"
                                            placeholder="0"
                                            value={addCoffeeAmount}
                                            onChange={(e) => setAddCoffeeAmount(e.target.value)}
                                        />
                                        <span className="unit">&nbsp;&nbsp;g</span>
                                    </div>

                                    <label htmlFor="coffee-price">Total Price</label>
                                    <div className="input-with-unit">
                                        <input
                                            id="coffee-price"
                                            type="number"
                                            placeholder="0.00"
                                            value={coffeePrice}
                                            onChange={(e) => setCoffeePrice(e.target.value)}
                                        />
                                        <span className="unit">&nbsp;&nbsp;€</span>
                                    </div>
                                    
                                        <div align="center" className="transaction-date-container">
                                            <input
                                                id="transaction-date"
                                                type="date"
                                                value={transactionDate}
                                                onChange={(e) => setTransactionDate(e.target.value)}
                                                className="transaction-date-input"
                                            />
                                        </div>
                                    
                                    <button 
                                    onClick={handleAddCoffee} 
                                    className="submit-coffee-button" 
                                    disabled={isSubmitting} 
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                </button>
                                </div>
                            </section>
                        )}

{isAdmin && showManualTransactionForm && (
    <>
        <div className="overlay" onClick={handleHideManualTransactionForm}></div>
        <section className="manual-transaction-section">
            <h2>Add Manual Transaction</h2>
            <div className="manual-transaction-form">
                <label htmlFor="manual-transaction-user">User</label>
                <select
                    id="manual-transaction-user"
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                >
                    {data.people.map((person, index) => (
                        <option key={index} value={person.name}>
                            {person.name}
                        </option>
                    ))}
                </select>

                <label htmlFor="manual-transaction-amount">Amount (€)</label>
                <input
                    id="manual-transaction-amount"
                    type="number"
                    value={manualTransactionAmount}
                    onChange={(e) => setManualTransactionAmount(e.target.value)}
                    placeholder="0.00"
                />

                <label htmlFor="manual-transaction-message">Message</label>
                <input
                    id="manual-transaction-message"
                    type="text"
                    value={manualTransactionMessage}
                    onChange={(e) => setManualTransactionMessage(e.target.value)}
                    placeholder="Enter a transaction message"
                />

                <div align="center" className="transaction-date-container">
                    <input
                        id="transaction-date"
                        type="date"
                        value={transactionDate}
                        onChange={(e) => setTransactionDate(e.target.value)}
                        className="transaction-date-input"
                    />
                </div>

                <button 
                    onClick={handleAddManualTransaction}
                    className="submit-transaction-button"
                >
                    Submit Transaction
                </button>
            </div>
        </section>
    </>
)}

{showCommentForm && (
                        <>
                            <div className="overlay" onClick={() => setShowCommentForm(false)}></div>
                            <div className="comment-form-overlay">
                                <input
                                    type="text"
                                    placeholder="Add a comment"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                />
                                <button onClick={handleCommentSubmit}>Submit</button>
                            </div>
                        </>
                    )}

                        {showAddUserOverlay && (
                            <>
                                <div className="overlay" onClick={() => setShowAddUserOverlay(false)}></div>
                                <section className="add-user-section-overlay">
                                    <h2>Add New User</h2>
                                    <input 
                                        type="text" 
                                        placeholder="New User Name" 
                                        value={newUserName}
                                        onChange={(e) => setNewUserName(e.target.value)}
                                        className="add-user-input"
                                    />
                                    <button onClick={handleAddUser} className="confirm-user-button">Confirm</button>
                                </section>
                            </>
                        )}
                    </div>
                    <div className="box">
                        <section className="transaction-history">
                            <div align="center">
                                <input
                                    type="text"
                                    placeholder="Search ..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="search-input"
                                />
                                <button onClick={downloadJsonFile} className="download-button">
                                    Download Log File
                                </button>&nbsp;
                                <button onClick={handleLogout} className="download-button">
                                    Logout
                                </button>&nbsp;
                                <button onClick={handleAdminClick} className="download-button">
                                    Edit
                                </button>
                            </div>
                            <ul className="transaction-list">
                                {filteredTransactions.length > 0 ? (
                                    filteredTransactions.reverse().map((transaction, index) => (
                                        <li key={index}>
                                            <span>
                                                {new Date(transaction.date).toLocaleDateString('de-DE', { 
                                                    weekday: 'short',
                                                    day: '2-digit', 
                                                    month: '2-digit', 
                                                    year: 'numeric'
                                                })} &nbsp;&nbsp; 
                                                {transaction.message}
                                            </span>
                                            {isAdmin && ( 
                                                <button 
                                                    className="transaction-delete-button" 
                                                    onClick={() => handleDeleteTransaction(transaction.id)}
                                                >
                                                    X
                                                </button>
                                            )}
                                        </li>
                                    ))
                                ) : (
                                    <p align="center">No transactions found.</p>
                                )}
                            </ul>
                        </section>
                    </div>
                </>
            )}
        </div>
    );
}

export default App;
